import { TechnologyTrlAnalysisChatKind, TechnologyTrlAnalysisNestedSelectDto } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { ConfidenceScore } from '@components/shared/confidence-score.component';
import { ExpandableText } from '@components/shared/expandable-text.component';
import { FieldProvenanceBadge } from '@components/shared/field-provenance-badge.component';
import { LlmChatModal } from '@components/shared/llm-chat/llm-chat-modal.component';
import { Tile } from '@components/shared/tile.component';
import { Button } from '@components/ui/button';
import { errorToast, successToast } from '@hooks/toast.hook';
import { useGetTechnology, useSaveTechnologyTrlAnalysisSummary } from '@queries/technology.query';
import { useGetCurrentUser } from '@queries/users.query';
import { isChildOrganizationView as getIsChildOrganizationView } from '@utils/parent-organization.util';
import { Loader2Icon, SparklesIcon } from 'lucide-react';
import { FC, useMemo, useState } from 'react';
import { TRL_ANALYSIS_PROMPT_GUIDELINES } from '../../../../../../constants/prompts.constants';
import { getTechnologyTrlAnalysisChatEndpoint } from '../../../../../../constants/technology-endpoints.constants';
import { StaleAssessmentTooltip } from './stale-assessment-tooltip.component';
import { TrlAnalysisSuggestionContent } from './trl-analysis-suggestion.component';
import { parseTrlAnalysisSummaryData } from './trl-analysis-suggestion.util';

type TrlAnalysisTileProps = {
  assessmentId: string | null;
  analysis: TechnologyTrlAnalysisNestedSelectDto | null;
  isLatestAssessment: boolean;
  isLoading?: boolean;
  technologyId: string;
};

const getCreateDescription = (technologyName?: string) => (
  <>
    Use Tyr&apos;s AI assistant to analyze the TRL score for <b>{technologyName || 'this technology'}</b>. All relevant
    metadata is used automatically — generate a draft, then refine it in conversation until it is ready to accept.
  </>
);

const getUpdateDescription = (technologyName?: string) => (
  <>
    The current TRL analysis for <b>{technologyName || 'this technology'}</b> is shown on the right. Describe the
    changes you want, select text in it to suggest edits, or regenerate it from scratch.
  </>
);

export const TrlAnalysisTile: FC<TrlAnalysisTileProps> = (props) => {
  const { analysis, assessmentId, isLatestAssessment, isLoading, technologyId } = props;
  const [isChatOpen, setIsChatOpen] = useState(false);
  const { isInnovator, isSelectedCompanyParentOrganization, selectedCompany } = useGetCurrentUser();
  const { data: technology, isPending: isTechnologyPending } = useGetTechnology(technologyId);
  // Only allow generation when we have an assessment to generate for
  const isChildOrganizationView = getIsChildOrganizationView({
    isSelectedCompanyParentOrganization,
    selectedCompanyId: selectedCompany?.id,
    technologyCompanyId: technology?.company?.id,
  });
  const canEdit = isInnovator && Boolean(assessmentId) && !isChildOrganizationView;

  const { isPending: isSaving, mutateAsync: saveSummary } = useSaveTechnologyTrlAnalysisSummary();

  const trlAnalysisSummary = analysis?.trlAnalysisSummary;
  const hasContent = Boolean(trlAnalysisSummary?.scoreAnalysis);
  const confidence = trlAnalysisSummary?.confidence ?? null;
  const isUpdateMode = hasContent;

  const initialSuggestionData = useMemo<Record<string, unknown> | undefined>(
    () => (isUpdateMode && trlAnalysisSummary ? { ...trlAnalysisSummary } : undefined),
    [isUpdateMode, trlAnalysisSummary],
  );

  const handleAcceptData = async (value: Record<string, unknown>, meta?: { logId?: string }): Promise<boolean> => {
    if (!assessmentId) return false;

    const parsed = parseTrlAnalysisSummaryData(value);

    if (!parsed) {
      errorToast({ description: 'The accepted draft is missing required fields.' });
      return false;
    }

    try {
      // Accepting a chat draft marks the field AI-assisted (ADR-0007).
      await saveSummary({
        assessmentId,
        provenance: { llmLogId: meta?.logId },
        technologyId,
        trlAnalysisSummary: parsed,
      });
    } catch {
      // The API hook already toasts persist failures; keeping the modal open
      // preserves the reviewed draft.
      return false;
    }

    successToast({ description: 'Updated TRL Analysis' });
    return true;
  };

  const renderDataArtifact = (value: Record<string, unknown>) => {
    const parsed = parseTrlAnalysisSummaryData(value);
    if (!parsed) return null;

    return <TrlAnalysisSuggestionContent summary={parsed} />;
  };

  const isTileLoading = isLoading || isTechnologyPending;

  return (
    <>
      <Tile
        emptyState={{
          title: 'No TRL Analysis',
          action:
            canEdit &&
            (isLatestAssessment ? (
              <Button
                className="mx-auto w-fit"
                onClick={() => setIsChatOpen(true)}
                variant="link"
              >
                Analyze TRL
                <SparklesIcon className="size-4" />
              </Button>
            ) : (
              <StaleAssessmentTooltip className="mx-auto block w-fit">
                <Button
                  disabled
                  variant="link"
                >
                  Analyze TRL
                  <SparklesIcon className="size-4" />
                </Button>
              </StaleAssessmentTooltip>
            )),
        }}
        hoverReveal
        isEmpty={!hasContent}
        isLoading={isTileLoading}
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ConfidenceScore
                design="box"
                score={confidence}
                size="small"
              />
              <div className="flex flex-col leading-tight">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-800">TRL Analysis</span>
                  <FieldProvenanceBadge provenance={analysis?.provenance?.trlAnalysisSummary} />
                </div>
                <span className="text-xs text-gray-500">Confidence Score</span>
              </div>
            </div>
            {canEdit && (
              <div className="flex items-center gap-2">
                {isLatestAssessment ? (
                  <IconButton.HoverReveal
                    ariaLabel="Update TRL analysis"
                    forceVisible={isSaving}
                    icon={isSaving ? <Loader2Icon className="animate-spin" /> : <SparklesIcon />}
                    onClick={() => setIsChatOpen(true)}
                    variant="secondary"
                  />
                ) : (
                  <StaleAssessmentTooltip>
                    <IconButton.HoverReveal
                      ariaLabel="Update TRL analysis"
                      disabled
                      icon={<SparklesIcon />}
                      variant="secondary"
                    />
                  </StaleAssessmentTooltip>
                )}
              </div>
            )}
          </div>

          {trlAnalysisSummary ? (
            <ExpandableText
              className="text-sm text-gray-600"
              maxLines={5}
              text={trlAnalysisSummary.scoreAnalysis}
            />
          ) : (
            <p className="text-sm text-gray-500">Generate an analysis to view insights.</p>
          )}
        </div>
      </Tile>

      <LlmChatModal
        apiEndpoint={getTechnologyTrlAnalysisChatEndpoint(technologyId)}
        data={{
          ...(assessmentId && { assessmentId }),
          kind: TechnologyTrlAnalysisChatKind.TRL_SUMMARY,
        }}
        description={isUpdateMode ? getUpdateDescription(technology?.name) : getCreateDescription(technology?.name)}
        generateDraftLabel={isUpdateMode ? 'Regenerate' : 'Analyze TRL'}
        generateDraftMessage={
          isUpdateMode ? 'Regenerate the TRL analysis from scratch.' : 'Generate a TRL analysis for this technology.'
        }
        information={TRL_ANALYSIS_PROMPT_GUIDELINES}
        initialSuggestionData={initialSuggestionData}
        isOpen={isChatOpen}
        onAcceptData={handleAcceptData}
        onClose={() => setIsChatOpen(false)}
        renderDataArtifact={renderDataArtifact}
        title="TRL Analysis"
      />
    </>
  );
};
